export type TaskSortField =
  'created' | 'dueDate' | 'estimatedValue' | 'status' | 'title';

export type TaskSortOrder = 'asc' | 'desc';

/** `TaskService.findAll`'s sort names; a typo would silently sort on nothing. */
type TaskApiSortKey =
  | 'created'
  | 'dueDate'
  | 'estimatedValue'
  | 'resolved'
  | 'statusRank'
  | 'title';

type TaskApiSort = `${TaskApiSortKey}:${TaskSortOrder}`;

export interface TaskSortSelection {
  field: TaskSortField;
  order: TaskSortOrder;
}

export interface TaskTabSort {
  default: TaskSortSelection;
  /** Offered in the field dropdown, in this order. */
  fields: TaskSortField[];
  /** Applied ahead of the member's choice, e.g. resolved claims last. */
  pinned?: TaskApiSort[];
}

/**
 * `sortByKey` is the task list's `sortBy` name. `defaultOrder` is applied when
 * the member picks the field, so value and recency lead with the largest and
 * newest. `tiebreaks` keep a fixed direction whichever way the member sorts,
 * ending in `created` so paging stays stable.
 */
export const TASK_SORT_FIELDS: Record<
  TaskSortField,
  {
    defaultOrder: TaskSortOrder;
    label: string;
    sortByKey: TaskApiSortKey;
    tiebreaks: TaskApiSort[];
  }
> = {
  created: {
    defaultOrder: 'desc',
    label: 'Date Created',
    sortByKey: 'created',
    tiebreaks: ['title:asc'],
  },
  dueDate: {
    defaultOrder: 'asc',
    label: 'Due Date',
    sortByKey: 'dueDate',
    tiebreaks: ['estimatedValue:desc', 'title:asc', 'created:asc'],
  },
  estimatedValue: {
    defaultOrder: 'desc',
    label: 'Est. Value',
    sortByKey: 'estimatedValue',
    tiebreaks: ['title:asc', 'created:asc'],
  },
  status: {
    defaultOrder: 'asc',
    label: 'Status',
    sortByKey: 'statusRank',
    tiebreaks: ['title:asc', 'created:asc'],
  },
  title: {
    defaultOrder: 'asc',
    label: 'Alphabetical',
    sortByKey: 'title',
    tiebreaks: ['created:asc'],
  },
};

export const TASK_SORT_ORDER_LABELS: Record<TaskSortOrder, string> = {
  asc: 'Ascending',
  desc: 'Descending',
};

export const TASK_SORT_PARAMS = {
  field: 'tasksSort',
  order: 'tasksOrder',
} as const;

export const isTaskSortOrder = (value: null | string): value is TaskSortOrder =>
  value === 'asc' || value === 'desc';

export const buildTaskSortBy = (
  sort: TaskTabSort,
  { field, order }: TaskSortSelection,
) => {
  const { sortByKey, tiebreaks } = TASK_SORT_FIELDS[field];

  return [...(sort.pinned ?? []), `${sortByKey}:${order}`, ...tiebreaks].join(
    ',',
  );
};
